// Card compatta prodotto con quantità, prezzo di riga e rimozione
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Euro, Trash2, Battery, Package } from 'lucide-react';
import { FADE_IN_ANIMATION, TYPE_ICONS, TYPE_COLORS, getKeySpec } from './constants';
import { NumericStepper } from './NumericStepper';

export function CompactProductCard({
  item,
  quantity,
  onQuantityChange,
  onRemove,
  readOnly = false
}) {
  const catalogItem = item.catalog_item;
  if (!catalogItem) return null;

  const TypeIcon = TYPE_ICONS[catalogItem.type_code] || Package;
  const colors = TYPE_COLORS[catalogItem.type_code] || { bg: 'bg-zinc-50', border: 'border-zinc-200', icon: 'text-zinc-500' };
  const isHybrid = catalogItem.type_code === 'INVERTER_IBRIDO';
  const keySpec = getKeySpec(catalogItem);

  const lineTotal = catalogItem.prezzo_listino ? catalogItem.prezzo_listino * quantity : null;

  return (
    <motion.div
      {...FADE_IN_ANIMATION}
      className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 border border-zinc-200 rounded-xl bg-white hover:border-zinc-300 hover:shadow-md transition-all group"
      data-testid={`product-card-${catalogItem.id}`}
    >
      {/* Sinistra: icona + dettagli */}
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className={`w-12 h-12 rounded-lg ${colors.bg} ${colors.border} border flex items-center justify-center shrink-0 relative`}>
          <TypeIcon className={`h-6 w-6 ${colors.icon}`} />
          {isHybrid && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-indigo-500 rounded-full flex items-center justify-center">
              <Battery className="h-2.5 w-2.5 text-white" />
            </div>
          )}
        </div>
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-zinc-500 uppercase tracking-wider">
              {catalogItem.brand}
            </span>
            {isHybrid && (
              <Badge className="text-[10px] px-1.5 py-0 h-4 bg-indigo-100 text-indigo-700 border-0">
                IBRIDO
              </Badge>
            )}
          </div>
          <span className="text-base font-semibold text-zinc-900 truncate">
            {catalogItem.model}
          </span>
          {keySpec && (
            <Badge variant="secondary" className="mt-1 w-fit font-mono text-xs bg-zinc-100 text-zinc-700">
              {keySpec}
            </Badge>
          )}
        </div>
      </div>

      {/* Destra: quantità + prezzo + azioni */}
      <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end">
        {!readOnly ? (
          <NumericStepper
            value={quantity}
            onChange={onQuantityChange}
            min={1}
            max={999}
            testId={`card-${catalogItem.id}-stepper`}
          />
        ) : (
          <Badge variant="outline" className="font-mono text-sm px-3 py-1">
            x{quantity}
          </Badge>
        )}

        {lineTotal && (
          <div className="text-right min-w-[80px]">
            <span className="text-sm font-semibold text-zinc-900 flex items-center justify-end gap-1">
              <Euro className="h-3.5 w-3.5" />
              {lineTotal.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
            </span>
          </div>
        )}

        {!readOnly && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="text-zinc-400 hover:text-rose-500 hover:bg-rose-50 transition-colors opacity-0 group-hover:opacity-100"
            data-testid={`remove-component-button-${catalogItem.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </motion.div>
  );
}
